import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  Stack,
  Chip
} from '@mui/material';
import { ErrorOutline, Refresh, Upload } from '@mui/icons-material';
import { logger } from '../utils/logger';


interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

export class LessonPlanErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `lesson_plan_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.error('LessonPlanErrorBoundary caught an error:', error, errorInfo);
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to external service in production
    if (process.env.NODE_ENV === 'production') {
      this.logErrorToService(error, errorInfo);
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    try {
      logger.error('Lesson plan error logged to service:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        errorId: this.state.errorId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        feature: 'lesson-plan-management'
      });
    } catch (loggingError) {
      logger.error('Failed to log lesson plan error to service:', loggingError);
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });

    // Call custom retry handler if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            p: 3,
            bgcolor: 'background.default',
            borderRadius: 2
          }}
        >
          <Card variant="outlined" sx={{ borderColor: 'error.main' }}>
            <CardContent>
              <Stack spacing={3} alignItems="center" textAlign="center">
                <ErrorOutline sx={{ fontSize: 48, color: 'error.main' }} />
                
                <Box>
                  <Typography variant="h6" gutterBottom color="error">
                    Lỗi ql giáo án
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Đã xảy ra lỗi khi xử lý giáo án. Vui lòng thử lại.
                  </Typography>
                </Box>

                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Alert severity="error" sx={{ width: '100%' }}>
                    <AlertTitle>Chi tiết lỗi (Development)</AlertTitle>
                    <Typography variant="body2" component="pre" sx={{ 
                      whiteSpace: 'pre-wrap', 
                      fontSize: '0.875rem',
                      fontFamily: 'monospace'
                    }}>
                      {this.state.error.message}
                    </Typography>
                    {this.state.error.stack && (
                      <Typography variant="body2" component="pre" sx={{ 
                        whiteSpace: 'pre-wrap', 
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        mt: 1
                      }}>
                        {this.state.error.stack}
                      </Typography>
                    )}
                  </Alert>
                )}

                <Box>
                  <Chip 
                    label={`Mã lỗi: ${this.state.errorId}`} 
                    size="small" 
                    color="error" 
                    variant="outlined"
                  />
                </Box>

                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={this.handleRetry}
                    sx={{ flex: 1 }}
                  >
                    Thử lại
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Upload />}
                    onClick={this.handleRefresh}
                    sx={{ flex: 1 }}
                  >
                    Tải lại
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default LessonPlanErrorBoundary;
